import { Fragment, memo } from 'react'
import { useLocation } from 'react-router-dom'

import { List, makeStyles, Box } from '@material-ui/core'

import { ReactComponent as GuiddeIcon } from 'assets/icons/icon-g.svg'
import GuiddeLogoRed from 'assets/icons/guidde_red.svg'

import { useMenuStatus } from 'UI/Components'
import { DrawerBody, DrawerLink, DrawerInviteItem, MenuProfile } from './common'

import { useOrgFeatureFlags, useGlobalFeatureFlags, useRoles, useServiceUsage } from 'hooks'
import { uiId } from 'modules'

import { MENU, MENU_ADDITIONAL, MenuListType } from 'app/menu'
import { paths } from 'app/paths'
import { SpaceMenuItem } from './SpaceMenuItem'

const useStyles = makeStyles({
    wrapper: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        height: '100%',
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
            width: '20px'
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent'
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#D8D8D8',
            borderRadius: '20px',
            border: '6px solid transparent',
            backgroundClip: 'content-box'
        }
    }
})
type LinkDrawerProps = {
    menu: MenuListType
}

const MAP_LOCKS = {
    [paths.activity]: 'activityFeedLocked',
    [paths.insights]: 'insightsLocked'
} as const

const LinkDrawer = ({ menu }: LinkDrawerProps) => {
    const location = useLocation()

    const orgFeatureFlags = useOrgFeatureFlags()
    const globalFeatureFlags = useGlobalFeatureFlags()
    const serviceUsage = useServiceUsage()

    const { userRoles, isSuperAdmin } = useRoles()
    const { isFreePlan } = useServiceUsage()

    const showBrandKitEndIcon = isFreePlan && !isSuperAdmin

    return (
        <>
            {menu.map((link, i) => {
                const currentLink = location?.pathname

                const isActive =
                    currentLink === link.path ||
                    link.path.split('/')[1] === currentLink.split('/')[1]

                if (link.role && !Array.isArray(link.role) && !userRoles[link.role]) {
                    return null
                }

                if (Array.isArray(link.role) && link.role.every(r => !userRoles[r])) {
                    return null
                }

                // Check for valid feature flag
                if (
                    link.feature &&
                    !orgFeatureFlags?.[link.feature as keyof typeof useOrgFeatureFlags] &&
                    !globalFeatureFlags?.[link.feature as keyof typeof useGlobalFeatureFlags]
                ) {
                    return null
                }

                const StartIcon = link.startIcon
                // hardcoded rule for showing second icon specially only for a brand kit menu item based on plan type
                let EndIcon =
                    showBrandKitEndIcon && link.path === paths.brandKit ? link.endIcon : null

                // check if icon is custom or imported from material-ui and select active color
                // @ts-ignore @TODO: TS-FIX
                const activeColor = StartIcon.type ? 'primary' : '#cd0000'

                if (serviceUsage[MAP_LOCKS[link.path as keyof typeof MAP_LOCKS]]) {
                    return null
                }

                if (link.path === paths.spaces) return <SpaceMenuItem key={link.path + i} />

                return (
                    <DrawerLink
                        key={link.path + i}
                        divider={link.showDivider}
                        id={uiId(`menu-${link.title.replace(' ', '-').toLowerCase()}`)}
                        to={{
                            pathname: link.path,
                            state: { prevPath: location.pathname }
                        }}
                        isActive={isActive}
                        endIcon={
                            EndIcon ? (
                                <EndIcon
                                    style={{
                                        color: '#cb0000'
                                    }}
                                />
                            ) : null
                        }
                        startIcon={
                            <StartIcon style={{ color: isActive ? activeColor : 'inherit' }} />
                        }
                        title={link.title}
                    />
                )
            })}
        </>
    )
}

export const LaptopDrawer = memo(() => {
    const classes = useStyles()

    const { isMenuClosed } = useMenuStatus()

    return (
        <DrawerBody>
            <Box className={classes.wrapper}>
                <List>
                    <Box pt={3} pl={2} minHeight={65}>
                        {isMenuClosed ? (
                            <GuiddeIcon />
                        ) : (
                            <img
                                style={{
                                    width: '100px',
                                    marginLeft: '7px'
                                }}
                                src={GuiddeLogoRed}
                                alt="red logo"
                            />
                        )}
                    </Box>
                </List>

                <List
                    style={{
                        paddingTop: 0,
                        display: 'flex',
                        justifyContent: 'space-between',
                        flexDirection: 'column',
                        height: '100%',
                        flexGrow: 1
                    }}
                >
                    <Box>
                        <LinkDrawer menu={MENU} />
                    </Box>
                    <Box>
                        <LinkDrawer menu={MENU_ADDITIONAL} />
                        <DrawerInviteItem />
                    </Box>
                </List>
            </Box>

            <MenuProfile />
        </DrawerBody>
    )
})
